// A distribution "domain" tells the histogram engine how to read/write the predicate value, format axis + handle
// labels, and map the row's operator onto the numeric highlight vocabulary (gt/gte/lt/lte/eq/neq/btw/nbtw).
// The engine (NumericDistribution) stays domain-agnostic; these adapters make one histogram serve numbers
// ($ / count), recency ("in the last N days"), forward windows ("in the next N days"), and calendar dates.

import dayjs from 'dayjs';

import type { ConditionValue, DistributionEncoding, RelativeWindow, TimeUnit, ValueShape } from '../../types';
import type { DisplayUnit } from '../types';
import { formatValue } from './format';

export interface DistributionDomain {
  /** Which numeric encoding this domain READS. The guard that stops a control rendering under the wrong
   *  operator compares against this rather than re-deriving it from the value shape: a parallel switch
   *  fails OPEN on any operator nobody remembered to add to it. */
  encoding: DistributionEncoding;
  /**
   * The smallest change this domain can WRITE, in its own units. Absent ⇒ no floor.
   *
   * A relative window stores `{ interval, unit }`, so a weeks-based window rounds any day count back to
   * whole weeks on write. A one-day step then reads straight back as the value it started from and the
   * handle never moves, so the step has to be at least the granularity a write survives.
   */
  granularity?: number;
  /** Current handle value(s) read from the predicate value (finite, or null when unset). */
  read: (v: ConditionValue) => { single: number | null; lo: number | null; hi: number | null };
  /** The bare value a single-handle commit writes. */
  writeSingle: (n: number) => ConditionValue;
  /** The bare value a range commit writes. */
  writeRange: (lo: number, hi: number) => ConditionValue;
  /** Axis + handle label for a domain number. */
  format: (n: number) => string;
  /** Map the row's operator onto the numeric vocabulary the engine highlights against. */
  normalizeOp: (op: string) => string;
}

const finite = (n: number | null | undefined): number | null =>
  typeof n === 'number' && Number.isFinite(n) ? n : null;

/** Read a scalar-or-2-tuple predicate value into handle positions, mapping each element through the domain's
 *  `toScalar` (the one per-domain difference) — `single` is set only for a bare scalar, `lo`/`hi` only for a
 *  tuple. Shared by the number + date domains so the read plumbing (and its element casts) lives in one place. */
function scalarRead(toScalar: (raw: unknown) => number | null): DistributionDomain['read'] {
  return (v) =>
    Array.isArray(v)
      ? { single: null, lo: toScalar(v[0]), hi: toScalar(v[1]) }
      : { single: toScalar(v), lo: null, hi: null };
}

const readNumber = scalarRead((raw) => finite(typeof raw === 'number' ? raw : null));

/** Numbers — the default: reads `number` / `numberRange`, formats with the optional unit ($ / %). Array
 *  `size_*` item-count operators map to their bare comparison (size_gte→gte) for the highlight vocabulary. */
export function numberDomain(unit?: DisplayUnit): DistributionDomain {
  return {
    encoding: 'number',
    read: readNumber,
    writeSingle: (n) => n,
    writeRange: (lo, hi) => [lo, hi],
    format: (n) => formatValue(n, unit),
    normalizeOp: (op) => (op.startsWith('size_') ? op.slice(5) : op),
  };
}

const DAYS_PER_UNIT: Record<TimeUnit, number> = { days: 1, weeks: 7, months: 30 };

function windowToDays(w: RelativeWindow | undefined): number | null {
  if (!w || !Number.isFinite(w.interval)) return null;
  return w.interval * DAYS_PER_UNIT[w.unit];
}

/** Convert a dragged day-count back into `unit`, clamped to a whole ≥ 1 (keeps a weeks/months window's unit). */
const daysToUnit = (days: number, unit: TimeUnit): number => Math.max(1, Math.round(days / DAYS_PER_UNIT[unit]));

/** Humanized day count — "30 d", "4 wk", "6 mo" (compact for the small axis/handle text). */
function humanizeDays(n: number): string {
  const d = Math.round(n);
  if (d >= 30 && d % 30 === 0) return `${d / 30} mo`;
  if (d >= 7 && d % 7 === 0) return `${d / 7} wk`;
  return `${d} d`;
}

export const isWindow = (v: ConditionValue): v is RelativeWindow =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

const readWindowDays = (v: ConditionValue) => ({
  single: windowToDays(isWindow(v) ? v : undefined),
  lo: null,
  hi: null,
});

/** Past recency — bins are days **ago**. `inlast` → lte (within N); `notinlast` → gt (older than N). */
export function recencyDomain(unit: TimeUnit = 'days'): DistributionDomain {
  return {
    encoding: 'days_ago',
    granularity: DAYS_PER_UNIT[unit],
    read: readWindowDays,
    writeSingle: (n) => ({ interval: daysToUnit(n, unit), unit }),
    writeRange: (_lo, hi) => ({ interval: daysToUnit(hi, unit), unit }),
    format: humanizeDays,
    normalizeOp: (op) => {
      if (op === 'inlast') return 'lte';
      if (op === 'notinlast') return 'gt';
      return op;
    },
  };
}

/** Forward window — bins are days **ahead**. `innext` → lte (within the next N days). Host supplies forward bins. */
export function forwardWindowDomain(unit: TimeUnit = 'days'): DistributionDomain {
  return {
    encoding: 'days_ahead',
    granularity: DAYS_PER_UNIT[unit],
    read: readWindowDays,
    writeSingle: (n) => ({ interval: daysToUnit(n, unit), unit }),
    writeRange: (_lo, hi) => ({ interval: daysToUnit(hi, unit), unit }),
    format: (n) => `+${humanizeDays(n)}`,
    normalizeOp: (op) => (op === 'innext' ? 'lte' : op),
  };
}

/** Map a relative-window operator to the domain that matches its timeline (preserving the window `unit`). */
export function domainForRelativeWindow(operator: string, unit: TimeUnit = 'days'): DistributionDomain {
  return operator === 'innext' ? forwardWindowDomain(unit) : recencyDomain(unit);
}

// Calendar dates are carried as an integer day-index off the epoch, so the engine's numeric math (drag,
// clamp, step) works unchanged and round-trips exactly (no timezone off-by-one).
//
// `toDay` reads the epoch off UTC, deliberately, because the server puts the DISTRIBUTION BINS on the
// same axis and derives their indices the same way (`toDayIndex` in `src/lib/audience/rows.ts`). A
// local-time `dayjs(d).diff(EPOCH, 'day')` truncates instead of rounding and shifts by the viewer's
// offset, so west of UTC an instant-valued rule (`2026-08-14T00:00:00Z`, which the agent may well
// write) reads back one bin left of the bar it belongs to — the handle sitting off its own value.
// `fromDay`/`format` stay on dayjs: they take an integer index and print the calendar date, and local
// midnight plus N days names the same day as UTC index N.
const EPOCH = dayjs('1970-01-01');
const DAY_MS = 86_400_000;
const toDay = (d: string | null | undefined): number | null => finite(d ? Math.round(Date.parse(d) / DAY_MS) : null);
const fromDay = (n: number): string => EPOCH.add(Math.round(n), 'day').format('YYYY-MM-DD');

/** Absolute dates — the date operators already speak the engine's canonical lt/lte/gt/gte/btw, so no remap. */
export const dateDomain: DistributionDomain = {
  encoding: 'day_index',
  granularity: 1,
  read: scalarRead((raw) => toDay(typeof raw === 'string' ? raw : null)),
  writeSingle: (n) => fromDay(n),
  writeRange: (lo, hi) => [fromDay(lo), fromDay(hi)],
  format: (n) => EPOCH.add(Math.round(n), 'day').format('MMM D, YYYY'),
  normalizeOp: (op) => op,
};

/** Pick the domain for a value shape. Pass `operator` for `relative_window` so past vs forward timelines resolve. */
export function domainForShape(
  shape: ValueShape,
  unit?: DisplayUnit,
  operator?: string,
  windowUnit?: TimeUnit,
): DistributionDomain {
  switch (shape) {
    case 'relative_window':
      return domainForRelativeWindow(operator ?? 'inlast', windowUnit);
    case 'date':
    case 'date_range':
      return dateDomain;
    case 'number':
    case 'number_range':
    case 'none':
    case 'text':
    case 'single_enum':
    case 'multi_enum':
    case 'boolean':
      return numberDomain(unit);
    default: {
      const _exhaustive: never = shape;
      return _exhaustive;
    }
  }
}
